import { useState, useCallback } from 'react';

/**
 * Hook for managing all modal and dialog states in the application.
 * Centralizes modal logic to keep App.tsx cleaner and more maintainable.
 */

export interface ModalState {
  // Info modals
  showComingSoonModal: boolean;
  comingSoonFeature: string;
  showContactModal: boolean;
  showHelpModal: boolean;
  showPrivacyPolicyModal: boolean;
  showTermsOfServiceModal: boolean;
  showLegalNoticeModal: boolean;
  
  // Chart modals
  isLoadModalOpen: boolean;
  isLoadModalRefreshing: boolean;
  isSaveAsModalOpen: boolean;
  isEmptyChartModalOpen: boolean;
  isNameRequiredModalOpen: boolean;
  saveAsTitle: string;
  
  // Measure dialogs
  isInsertMeasuresDialogOpen: boolean;
  insertMeasuresDialogIndex: number;
  isDeleteMeasureDialogOpen: boolean;
  deleteMeasureDialogIndex: number;
  
  // Settings & Account
  isRightDockOpen: boolean;
  isUpgradeModalOpen: boolean;
  isSubscriptionModalOpen: boolean;
  upgradeTriggerFeature: string | undefined;
  isPremiumSuccessModalOpen: boolean;
  
  // Jam Session
  showJamSessionDialog: boolean;
  
  // Set List
  showSetListManager: boolean;
  showGigSetListSelector: boolean;
  
  // PDF Export
  showPDFExportModal: boolean;
  
  // WebRTC Debug
  showWebRTCDebugger: boolean;

  // MIDI Piano Recorder (MiniDaw)
  isMidiDawOpen: boolean;

  // New Chart choice (Chord Grid vs Piano Rec) - mobile 'New' entry point
  isNewChartChoiceModalOpen: boolean;
}

export interface ModalActions {
  // Coming Soon
  openComingSoon: (feature: string) => void;
  closeComingSoon: () => void;
  
  // Contact
  openContact: () => void;
  closeContact: () => void;
  
  // Help
  openHelp: () => void;
  closeHelp: () => void;
  
  // Privacy Policy
  openPrivacyPolicy: () => void;
  closePrivacyPolicy: () => void;
  
  // Terms of Service
  openTermsOfService: () => void;
  closeTermsOfService: () => void;
  
  // Legal Notice (Mentions Légales)
  openLegalNotice: () => void;
  closeLegalNotice: () => void;
  
  // Load Chart
  openLoadModal: () => void;
  closeLoadModal: () => void;
  setIsLoadModalRefreshing: (value: boolean) => void;
  
  // Save As
  openSaveAsModal: () => void;
  closeSaveAsModal: () => void;
  setSaveAsTitle: (title: string) => void;
  
  // Empty Chart
  openEmptyChartModal: () => void;
  closeEmptyChartModal: () => void;

  // Name Required (save without title)
  openNameRequiredModal: () => void;
  closeNameRequiredModal: () => void;

  // Insert Measures
  openInsertMeasuresDialog: (index: number) => void;
  closeInsertMeasuresDialog: () => void;
  
  // Delete Measure
  openDeleteMeasureDialog: (index: number) => void;
  closeDeleteMeasureDialog: () => void;
  
  // Settings (Right Dock)
  openSettings: () => void;
  closeSettings: () => void;
  
  // Upgrade Modal
  openUpgradeModal: (triggerFeature?: string) => void;
  closeUpgradeModal: () => void;
  
  // Subscription Modal
  openSubscriptionModal: () => void;
  closeSubscriptionModal: () => void;
  
  // Premium Success Modal
  openPremiumSuccessModal: () => void;
  closePremiumSuccessModal: () => void;
  
  // Jam Session Dialog
  openJamSessionDialog: () => void;
  closeJamSessionDialog: () => void;
  
  // Set List Manager
  openSetListManager: () => void;
  closeSetListManager: () => void;
  
  // Gig Set List Selector
  openGigSetListSelector: () => void;
  closeGigSetListSelector: () => void;
  
  // PDF Export Modal
  openPDFExportModal: () => void;
  closePDFExportModal: () => void;
  
  // WebRTC Debugger
  openWebRTCDebugger: () => void;
  closeWebRTCDebugger: () => void;
  toggleWebRTCDebugger: () => void;

  // MIDI Piano Recorder (MiniDaw)
  openMidiDaw: () => void;
  closeMidiDaw: () => void;

  // New Chart choice (Chord Grid vs Piano Rec)
  openNewChartChoiceModal: () => void;
  closeNewChartChoiceModal: () => void;
}

export function useModalState(): ModalState & ModalActions {
  // Info modals
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [showTermsOfServiceModal, setShowTermsOfServiceModal] = useState(false);
  const [showLegalNoticeModal, setShowLegalNoticeModal] = useState(false);
  
  // Chart modals
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [isLoadModalRefreshing, setIsLoadModalRefreshing] = useState(false);
  const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
  const [isEmptyChartModalOpen, setIsEmptyChartModalOpen] = useState(false);
  const [isNameRequiredModalOpen, setIsNameRequiredModalOpen] = useState(false);
  const [saveAsTitle, setSaveAsTitle] = useState('');
  
  // Measure dialogs
  const [isInsertMeasuresDialogOpen, setIsInsertMeasuresDialogOpen] = useState(false);
  const [insertMeasuresDialogIndex, setInsertMeasuresDialogIndex] = useState(0);
  const [isDeleteMeasureDialogOpen, setIsDeleteMeasureDialogOpen] = useState(false);
  const [deleteMeasureDialogIndex, setDeleteMeasureDialogIndex] = useState(0);
  
  // Settings & Account
  const [isRightDockOpen, setIsRightDockOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [upgradeTriggerFeature, setUpgradeTriggerFeature] = useState<string | undefined>(undefined);
  const [isPremiumSuccessModalOpen, setIsPremiumSuccessModalOpen] = useState(false);
  
  // Jam Session
  const [showJamSessionDialog, setShowJamSessionDialog] = useState(false);
  
  // Set List
  const [showSetListManager, setShowSetListManager] = useState(false);
  const [showGigSetListSelector, setShowGigSetListSelector] = useState(false);
  
  // PDF Export
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  
  // WebRTC Debug
  const [showWebRTCDebugger, setShowWebRTCDebugger] = useState(false);

  // MIDI Piano Recorder (MiniDaw)
  const [isMidiDawOpen, setIsMidiDawOpen] = useState(false);

  // New Chart choice (Chord Grid vs Piano Rec)
  const [isNewChartChoiceModalOpen, setIsNewChartChoiceModalOpen] = useState(false);

  // === Actions ===
  
  // Coming Soon
  const openComingSoon = useCallback((feature: string) => {
    setComingSoonFeature(feature);
    setShowComingSoonModal(true);
  }, []);
  const closeComingSoon = useCallback(() => setShowComingSoonModal(false), []);
  
  // Contact
  const openContact = useCallback(() => setShowContactModal(true), []);
  const closeContact = useCallback(() => setShowContactModal(false), []);
  
  // Help
  const openHelp = useCallback(() => setShowHelpModal(true), []);
  const closeHelp = useCallback(() => setShowHelpModal(false), []);
  
  // Privacy Policy
  const openPrivacyPolicy = useCallback(() => setShowPrivacyPolicyModal(true), []);
  const closePrivacyPolicy = useCallback(() => setShowPrivacyPolicyModal(false), []);
  
  // Terms of Service
  const openTermsOfService = useCallback(() => setShowTermsOfServiceModal(true), []);
  const closeTermsOfService = useCallback(() => setShowTermsOfServiceModal(false), []);

  // Legal Notice (Mentions Légales)
  const openLegalNotice = useCallback(() => setShowLegalNoticeModal(true), []);
  const closeLegalNotice = useCallback(() => setShowLegalNoticeModal(false), []);

  // Load Chart
  const openLoadModal = useCallback(() => setIsLoadModalOpen(true), []);
  const closeLoadModal = useCallback(() => setIsLoadModalOpen(false), []);
  
  // Save As
  const openSaveAsModal = useCallback(() => setIsSaveAsModalOpen(true), []);
  const closeSaveAsModal = useCallback(() => setIsSaveAsModalOpen(false), []);
  
  // Empty Chart
  const openEmptyChartModal = useCallback(() => setIsEmptyChartModalOpen(true), []);
  const closeEmptyChartModal = useCallback(() => setIsEmptyChartModalOpen(false), []);
  
  // Name Required
  const openNameRequiredModal = useCallback(() => setIsNameRequiredModalOpen(true), []);
  const closeNameRequiredModal = useCallback(() => setIsNameRequiredModalOpen(false), []);
  
  // Insert Measures
  const openInsertMeasuresDialog = useCallback((index: number) => {
    setInsertMeasuresDialogIndex(index);
    setIsInsertMeasuresDialogOpen(true);
  }, []);
  const closeInsertMeasuresDialog = useCallback(() => setIsInsertMeasuresDialogOpen(false), []);
  
  // Delete Measure
  const openDeleteMeasureDialog = useCallback((index: number) => {
    setDeleteMeasureDialogIndex(index);
    setIsDeleteMeasureDialogOpen(true);
  }, []);
  const closeDeleteMeasureDialog = useCallback(() => setIsDeleteMeasureDialogOpen(false), []);
  
  // Settings
  const openSettings = useCallback(() => setIsRightDockOpen(true), []);
  const closeSettings = useCallback(() => setIsRightDockOpen(false), []);
  
  // Upgrade Modal
  const openUpgradeModal = useCallback((triggerFeature?: string) => {
    setUpgradeTriggerFeature(triggerFeature);
    setIsUpgradeModalOpen(true);
  }, []);
  const closeUpgradeModal = useCallback(() => setIsUpgradeModalOpen(false), []);
  
  // Subscription Modal
  const openSubscriptionModal = useCallback(() => setIsSubscriptionModalOpen(true), []);
  const closeSubscriptionModal = useCallback(() => setIsSubscriptionModalOpen(false), []);
  
  // Premium Success Modal
  const openPremiumSuccessModal = useCallback(() => setIsPremiumSuccessModalOpen(true), []);
  const closePremiumSuccessModal = useCallback(() => setIsPremiumSuccessModalOpen(false), []);
  
  // Jam Session Dialog
  const openJamSessionDialog = useCallback(() => setShowJamSessionDialog(true), []);
  const closeJamSessionDialog = useCallback(() => setShowJamSessionDialog(false), []);
  
  // Set List Manager
  const openSetListManager = useCallback(() => setShowSetListManager(true), []);
  const closeSetListManager = useCallback(() => setShowSetListManager(false), []);
  
  // Gig Set List Selector
  const openGigSetListSelector = useCallback(() => setShowGigSetListSelector(true), []);
  const closeGigSetListSelector = useCallback(() => setShowGigSetListSelector(false), []);
  
  // PDF Export Modal
  const openPDFExportModal = useCallback(() => setShowPDFExportModal(true), []);
  const closePDFExportModal = useCallback(() => setShowPDFExportModal(false), []);
  
  // WebRTC Debugger
  const openWebRTCDebugger = useCallback(() => setShowWebRTCDebugger(true), []);
  const closeWebRTCDebugger = useCallback(() => setShowWebRTCDebugger(false), []);
  const toggleWebRTCDebugger = useCallback(() => setShowWebRTCDebugger(prev => !prev), []);

  // MIDI Piano Recorder (MiniDaw)
  const openMidiDaw = useCallback(() => setIsMidiDawOpen(true), []);
  const closeMidiDaw = useCallback(() => setIsMidiDawOpen(false), []);

  // New Chart choice (Chord Grid vs Piano Rec)
  const openNewChartChoiceModal = useCallback(() => setIsNewChartChoiceModalOpen(true), []);
  const closeNewChartChoiceModal = useCallback(() => setIsNewChartChoiceModalOpen(false), []);

  return {
    // State
    showComingSoonModal,
    comingSoonFeature,
    showContactModal,
    showHelpModal,
    showPrivacyPolicyModal,
    showTermsOfServiceModal,
    showLegalNoticeModal,
    isLoadModalOpen,
    isLoadModalRefreshing,
    isSaveAsModalOpen,
    isEmptyChartModalOpen,
    isNameRequiredModalOpen,
    saveAsTitle,
    isInsertMeasuresDialogOpen,
    insertMeasuresDialogIndex,
    isDeleteMeasureDialogOpen,
    deleteMeasureDialogIndex,
    isRightDockOpen,
    isUpgradeModalOpen,
    isSubscriptionModalOpen,
    upgradeTriggerFeature,
    isPremiumSuccessModalOpen,
    showJamSessionDialog,
    showSetListManager,
    showGigSetListSelector,
    showPDFExportModal,
    showWebRTCDebugger,
    isMidiDawOpen,
    isNewChartChoiceModalOpen,
    
    // Actions
    openComingSoon,
    closeComingSoon,
    openContact,
    closeContact,
    openHelp,
    closeHelp,
    openPrivacyPolicy,
    closePrivacyPolicy,
    openTermsOfService,
    closeTermsOfService,
    openLegalNotice,
    closeLegalNotice,
    openLoadModal,
    closeLoadModal,
    setIsLoadModalRefreshing,
    openSaveAsModal,
    closeSaveAsModal,
    setSaveAsTitle,
    openEmptyChartModal,
    closeEmptyChartModal,
    openNameRequiredModal,
    closeNameRequiredModal,
    openInsertMeasuresDialog,
    closeInsertMeasuresDialog,
    openDeleteMeasureDialog,
    closeDeleteMeasureDialog,
    openSettings,
    closeSettings,
    openUpgradeModal,
    closeUpgradeModal,
    openSubscriptionModal,
    closeSubscriptionModal,
    openPremiumSuccessModal,
    closePremiumSuccessModal,
    openJamSessionDialog,
    closeJamSessionDialog,
    openSetListManager,
    closeSetListManager,
    openGigSetListSelector,
    closeGigSetListSelector,
    openPDFExportModal,
    closePDFExportModal,
    openWebRTCDebugger,
    closeWebRTCDebugger,
    toggleWebRTCDebugger,
    openMidiDaw,
    closeMidiDaw,
    openNewChartChoiceModal,
    closeNewChartChoiceModal,
  };
}
